import { TeamAIMemberData, RollingPeriodInfo } from './teamAIDataService';
import { 
  arrayToCSV, 
  downloadCSV, 
  downloadPDF, 
  createPDF, 
  addTableToPDF,
  addPageBreakIfNeeded,
  formatTimeForDisplay,
  formatPercentageForDisplay,
  formatScoreForDisplay,
  generateFilename,
  getDataModeLabel
} from './exportUtils';

/**
 * Export service for Team AI Leaderboard board (TeamAISection component)
 */

export interface TeamAIExportOptions {
  dataMode: string;
  periodInfo: RollingPeriodInfo;
  sortBy: string;
}

/**
 * Prepare Team AI data for CSV export
 */
const prepareTeamAIDataForCSV = (teamAIData: TeamAIMemberData[]): any[] => {
  return teamAIData.map((member, index) => ({
    Rank: index + 1,
    'Team Member': member.name,
    'Email': member.email,
    'AI Effectiveness Score': formatScoreForDisplay(member.metrics.aiEffectivenessScore),
    'Cursor Efficiency %': formatPercentageForDisplay(member.metrics.cursorEfficiency),
    'AI Assistance Ratio %': formatPercentageForDisplay(member.metrics.aiAssistanceRatio),
    'Productivity Score': formatScoreForDisplay(member.metrics.productivityScore, 0),
    'AI Tool Effectiveness %': formatPercentageForDisplay(member.metrics.aiToolEffectiveness),
    'AI Adoption Score %': formatPercentageForDisplay(member.metrics.aiAdoptionScore),
    'Overall AI Adoption %': formatPercentageForDisplay(member.metrics.overallAIAdoption),
    'Total AI Time': formatTimeForDisplay(member.metrics.totalAITime),
    'Total Work Time': formatTimeForDisplay(member.metrics.totalWorkTime),
    'Cursor Time (Hours)': formatScoreForDisplay(member.metrics.cursorTimeHours),
    'AI Tool Time (Hours)': formatScoreForDisplay(member.metrics.aiToolTimeHours),
    'Non-AI Tool Time (Hours)': formatScoreForDisplay(member.metrics.nonAIToolTimeHours),
    'Unique AI Tools Count': member.metrics.uniqueAIToolsCount,
    'Primary AI Tool': member.metrics.primaryAITool,
    'Accepted Lines Added': member.metrics.acceptedLinesAdded,
    'Total Lines Added': member.metrics.totalLinesAdded
  }));
};

/**
 * Export Team AI Leaderboard data to CSV
 */
export const exportTeamAIToCSV = (
  teamAIData: TeamAIMemberData[],
  options: TeamAIExportOptions
): void => {
  if (teamAIData.length === 0) {
    alert('No Team AI data available to export');
    return;
  }

  try {
    const data = prepareTeamAIDataForCSV(teamAIData);
    const csvContent = arrayToCSV(data);
    
    const dataMode = getDataModeLabel(options.dataMode);
    const periodRange = `${options.periodInfo.startDate}_to_${options.periodInfo.endDate}`;
    
    const filename = generateFilename(
      `team_ai_leaderboard_${dataMode}_4week_rolling_${periodRange}`,
      'csv'
    );
    
    downloadCSV(csvContent, filename);
    console.log('✅ [EXPORT] Team AI Leaderboard CSV exported:', filename);
  } catch (error) {
    console.error('❌ [EXPORT] Failed to export Team AI Leaderboard CSV:', error);
    alert('Failed to export CSV. Please try again.');
  }
};

/**
 * Export Team AI Leaderboard data to PDF
 */
export const exportTeamAIToPDF = (
  teamAIData: TeamAIMemberData[],
  options: TeamAIExportOptions
): void => {
  if (teamAIData.length === 0) {
    alert('No Team AI data available to export');
    return;
  }

  try {
    const dataMode = getDataModeLabel(options.dataMode);
    const title = `Team AI Effectiveness Leaderboard - ${dataMode}`;
    const pdf = createPDF(title, 'landscape');

    // Add period information
    let currentY = 40;
    pdf.setFontSize(12);
    pdf.text(`Rolling 4-Week Period: ${options.periodInfo.startDate} to ${options.periodInfo.endDate}`, 20, currentY);
    currentY += 10;
    pdf.text(`Analysis Weeks: ${options.periodInfo.weekDates.join(', ')}`, 20, currentY);
    currentY += 10;
    pdf.text(`Sorted by: ${options.sortBy}`, 20, currentY);
    currentY += 10;
    
    pdf.setFontSize(10);
    pdf.text(`Total Team Members: ${teamAIData.length}`, 20, currentY);
    currentY += 8;
    
    const avgEffectiveness = teamAIData.reduce((sum, member) => sum + member.metrics.aiEffectivenessScore, 0) / teamAIData.length;
    pdf.text(`Average AI Effectiveness: ${formatScoreForDisplay(avgEffectiveness)}`, 20, currentY);
    currentY += 15;

    // Main Team AI Table
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Team AI Effectiveness Rankings', 20, currentY);
    currentY += 10;

    const headers = [
      'Rank',
      'Team Member',
      'AI Score',
      'Cursor Eff %',
      'AI Tool Eff %',
      'AI Adoption %',
      'Total AI Time',
      'Primary AI Tool'
    ];

    const tableData = teamAIData.map((member, index) => [
      String(index + 1),
      member.name,
      formatScoreForDisplay(member.metrics.aiEffectivenessScore),
      formatPercentageForDisplay(member.metrics.cursorEfficiency),
      formatPercentageForDisplay(member.metrics.aiToolEffectiveness),
      formatPercentageForDisplay(member.metrics.overallAIAdoption),
      formatTimeForDisplay(member.metrics.totalAITime),
      member.metrics.primaryAITool
    ]);

    currentY = addTableToPDF(pdf, headers, tableData, currentY, {
      styles: { fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 12 }, // Rank
        1: { cellWidth: 35 }, // Team Member
        2: { cellWidth: 20 }, // AI Score
        3: { cellWidth: 20 }, // Cursor Eff
        4: { cellWidth: 20 }, // AI Tool Eff
        5: { cellWidth: 20 }, // AI Adoption
        6: { cellWidth: 25 }, // Total AI Time
        7: { cellWidth: 30 }  // Primary AI Tool
      }
    }) + 20;

    // Detailed Metrics Table (top 10 members)
    if (teamAIData.length > 0) {
      currentY = addPageBreakIfNeeded(pdf, currentY, 80);
      
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Detailed Metrics (Top 10 Members)', 20, currentY);
      currentY += 10;

      const detailedHeaders = [
        'Rank',
        'Member',
        'Assist %',
        'Prod Score',
        'AI Tools',
        'Total Work',
        'Lines Added'
      ];

      const detailedTableData = teamAIData.slice(0, 10).map((member, index) => [
        String(index + 1),
        member.name,
        formatPercentageForDisplay(member.metrics.aiAssistanceRatio),
        formatScoreForDisplay(member.metrics.productivityScore, 0),
        String(member.metrics.uniqueAIToolsCount),
        formatTimeForDisplay(member.metrics.totalWorkTime),
        `${member.metrics.acceptedLinesAdded}/${member.metrics.totalLinesAdded}`
      ]);

      currentY = addTableToPDF(pdf, detailedHeaders, detailedTableData, currentY, {
        styles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 15 }, // Rank
          1: { cellWidth: 40 }, // Member
          2: { cellWidth: 20 }, // Assist %
          3: { cellWidth: 25 }, // Prod Score
          4: { cellWidth: 20 }, // AI Tools
          5: { cellWidth: 25 }, // Total Work
          6: { cellWidth: 30 }  // Lines Added
        }
      }) + 20;

      // AI Effectiveness Levels Legend
      currentY = addPageBreakIfNeeded(pdf, currentY, 40);
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('AI Effectiveness Levels', 20, currentY);
      currentY += 10;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      const levels = [
        '🟢 Expert (90+): Highly effective AI usage across all tools',
        '🟡 Power User (80-89): Strong AI adoption with good efficiency',
        '🟠 Proficient (70-79): Good AI usage with room for improvement',
        '🔴 Developing (60-69): Basic AI usage, significant growth potential',
        '🟤 Beginner (50-59): Limited AI adoption, needs training',
        '⚫ Minimal (0-49): Very low AI engagement'
      ];

      levels.forEach(level => {
        pdf.text(level, 20, currentY);
        currentY += 7;
      });
    }

    const periodRange = `${options.periodInfo.startDate}_to_${options.periodInfo.endDate}`;
    const filename = generateFilename(
      `team_ai_leaderboard_${dataMode}_4week_rolling_${periodRange}`,
      'pdf'
    );
    
    downloadPDF(pdf, filename);
    console.log('✅ [EXPORT] Team AI Leaderboard PDF exported:', filename);
  } catch (error) {
    console.error('❌ [EXPORT] Failed to export Team AI Leaderboard PDF:', error);
    alert('Failed to export PDF. Please try again.');
  }
};
